import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  HeartIcon, 
  ChatBubbleLeftIcon, 
  ShoppingBagIcon,
  BookOpenIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  BellIcon
} from '@heroicons/react/24/outline'

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()

  const isActive = (path: string) => location.pathname === path

  return (
    <nav className="nav-glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="text-2xl font-display font-bold gradient-text"
            >
              ConnectHub
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <NavLink to="/stories" icon={BookOpenIcon} text="Stories" isActive={isActive('/stories')} />
            <NavLink to="/dating" icon={HeartIcon} text="Dating" isActive={isActive('/dating')} />
            <NavLink to="/shop" icon={ShoppingBagIcon} text="Shop" isActive={isActive('/shop')} />
            <NavLink to="/chat" icon={ChatBubbleLeftIcon} text="Chat" isActive={isActive('/chat')} />
          </div>

          {/* Search and Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50" />
              <input
                type="text"
                placeholder="Search..."
                className="input-glass pl-10 pr-4 py-2 w-64 text-sm"
              />
            </div>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="relative p-2 rounded-xl hover:bg-white/10 transition-colors"
            >
              <BellIcon className="h-6 w-6" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-secondary-500 rounded-full"></span>
            </motion.button>

            <Link to="/profile">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="h-8 w-8 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 p-1"
              >
                <div className="h-full w-full rounded-full bg-dark-100 flex items-center justify-center">
                  <UserIcon className="h-4 w-4" />
                </div>
              </motion.div>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-xl hover:bg-white/10 transition-colors"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mt-4 pb-4 border-t border-white/10"
          >
            <div className="flex flex-col space-y-2 pt-4">
              <MobileNavLink to="/stories" icon={BookOpenIcon} text="Stories" />
              <MobileNavLink to="/dating" icon={HeartIcon} text="Dating" />
              <MobileNavLink to="/shop" icon={ShoppingBagIcon} text="Shop" />
              <MobileNavLink to="/chat" icon={ChatBubbleLeftIcon} text="Chat" />
              <MobileNavLink to="/profile" icon={UserIcon} text="Profile" />
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  )
}

interface NavLinkProps {
  to: string
  icon: React.ComponentType<any>
  text: string
  isActive?: boolean
}

const NavLink: React.FC<NavLinkProps> = ({ to, icon: Icon, text, isActive }) => (
  <Link to={to}>
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={`flex items-center space-x-2 px-3 py-2 rounded-xl transition-colors ${
        isActive
          ? 'bg-gradient-to-r from-primary-600/20 to-secondary-600/20 text-primary-300'
          : 'hover:bg-white/10'
      }`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{text}</span>
    </motion.div>
  </Link>
)

const MobileNavLink: React.FC<NavLinkProps> = ({ to, icon: Icon, text }) => (
  <Link to={to}>
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-white/10 transition-colors"
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{text}</span>
    </motion.div>
  </Link>
)

export default Navbar